import { supabase } from '../lib/supabase';

/**
 * Billing / paywall service.
 * Publishing a conference consumes an entitlement (first free, then a credit or
 * an active annual plan). Payments are manual: the user uploads a receipt, an
 * admin approves it, and credits/annual are granted. Enforcement lives in the
 * database (publish_conference / approve_payment RPCs + a trigger).
 */

const mapPlan = (p) => ({
  id: p?.id,
  name: p?.name,
  planType: p?.plan_type,
  credits: p?.credits,
  durationDays: p?.duration_days,
  priceDzd: p?.price_dzd != null ? Number(p?.price_dzd) : 0,
  description: p?.description,
  isActive: p?.is_active,
  sortOrder: p?.sort_order
});

const mapPayment = (p) => ({
  id: p?.id,
  userId: p?.user_id,
  planId: p?.plan_id,
  amountDzd: p?.amount_dzd != null ? Number(p?.amount_dzd) : null,
  receiptUrl: p?.receipt_url,
  referenceCode: p?.reference_code,
  status: p?.status,
  adminNote: p?.admin_note,
  createdAt: p?.created_at,
  reviewedAt: p?.reviewed_at,
  planName: p?.billing_plans?.name,
  user: p?.user_profiles
    ? {
        id: p?.user_profiles?.id,
        firstName: p?.user_profiles?.first_name,
        lastName: p?.user_profiles?.last_name,
        email: p?.user_profiles?.email
      }
    : null
});

export const billingService = {
  /** Active plans, ordered — used on the pricing page/section. */
  async getPlans({ includeInactive = false } = {}) {
    try {
      let q = supabase?.from('billing_plans')?.select('*')?.order('sort_order', { ascending: true });
      if (!includeInactive) q = q?.eq('is_active', true);
      const { data, error } = await q;
      if (error) throw error;
      return data?.map(mapPlan) || [];
    } catch (error) {
      console.error('Get plans error:', error);
      return [];
    }
  },

  /** Current user's entitlements (credits, free publishes, annual). */
  async getMyBilling() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;
      const { data, error } = await supabase
        ?.from('user_billing')?.select('*')?.eq('user_id', user?.id)?.maybeSingle();
      if (error) throw error;
      // No row yet = fresh user: 1 free publish, nothing else.
      return {
        freePublishesRemaining: data?.free_publishes_remaining ?? 1,
        credits: data?.credits ?? 0,
        annualUntil: data?.annual_until ?? null
      };
    } catch (error) {
      console.error('Get my billing error:', error);
      return { freePublishesRemaining: 1, credits: 0, annualUntil: null };
    }
  },

  /** True if the user can publish right now without paying. */
  async canPublish() {
    const b = await this.getMyBilling();
    if (!b) return false;
    const annualActive = b?.annualUntil && new Date(b?.annualUntil) > new Date();
    return annualActive || (b?.freePublishesRemaining || 0) > 0 || (b?.credits || 0) > 0;
  },

  /** Payment instructions (CCP/RIP/Baridi Mob). */
  async getPaymentSettings() {
    try {
      const { data, error } = await supabase
        ?.from('payment_settings')?.select('*')?.eq('id', 1)?.maybeSingle();
      if (error) throw error;
      return {
        ccpNumber: data?.ccp_number || '',
        ripNumber: data?.rip_number || '',
        baridimobNumber: data?.baridimob_number || '',
        instructions: data?.instructions || ''
      };
    } catch (error) {
      console.error('Get payment settings error:', error);
      return { ccpNumber: '', ripNumber: '', baridimobNumber: '', instructions: '' };
    }
  },

  /** Upload a receipt file to the private bucket, returns its storage path. */
  async uploadReceipt(file, userId) {
    const ext = (file?.name?.split('.')?.pop() || 'jpg').toLowerCase();
    const path = `${userId}/${Date.now()}.${ext}`;
    const { error } = await supabase?.storage?.from('payment-receipts')
      ?.upload(path, file, { cacheControl: '3600', upsert: false });
    if (error) throw error;
    return path;
  },

  /** Signed URL to view a receipt (admin or owner). */
  async getReceiptUrl(path, expiresIn = 3600) {
    if (!path) return null;
    const { data, error } = await supabase?.storage?.from('payment-receipts')
      ?.createSignedUrl(path, expiresIn);
    if (error) return null;
    return data?.signedUrl || null;
  },

  /** Submit a payment for a plan with an uploaded receipt. */
  async submitPayment({ planId, amountDzd, receiptFile }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      let receiptUrl = null;
      if (receiptFile) receiptUrl = await this.uploadReceipt(receiptFile, user?.id);

      const referenceCode = `CMP-${Date.now().toString(36).toUpperCase()}`;
      const { data, error } = await supabase?.from('payments')?.insert({
        user_id: user?.id,
        plan_id: planId,
        amount_dzd: amountDzd ?? null,
        receipt_url: receiptUrl,
        reference_code: referenceCode,
        status: 'pending'
      })?.select()?.single();

      if (error) throw error;
      return mapPayment(data);
    } catch (error) {
      console.error('Submit payment error:', error);
      throw error;
    }
  },

  /** The current user's payment history. */
  async getMyPayments() {
    try {
      const { data, error } = await supabase
        ?.from('payments')
        ?.select('*, billing_plans (name)')
        ?.order('created_at', { ascending: false });
      if (error) throw error;
      return data?.map(mapPayment) || [];
    } catch (error) {
      console.error('Get my payments error:', error);
      return [];
    }
  },

  /**
   * Publish a conference through the paywall RPC. Resolves to 'ok', or throws
   * with message 'payment_required' when the user has no entitlement left.
   */
  async publishConference(conferenceId, targetStatus = 'published') {
    const { data, error } = await supabase?.rpc('publish_conference', {
      p_conference_id: conferenceId,
      p_target_status: targetStatus
    });
    if (error) throw new Error(error?.message || 'publish_failed');
    return data;
  },

  // ── Admin ──

  /** Pending (and recent) payments for the admin approval queue. */
  async getAllPayments({ status } = {}) {
    try {
      let q = supabase
        ?.from('payments')
        ?.select('*, billing_plans (name), user_profiles!payments_user_id_fkey (id, first_name, last_name, email)')
        ?.order('created_at', { ascending: false });
      if (status) q = q?.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return data?.map(mapPayment) || [];
    } catch (error) {
      console.error('Get all payments error:', error);
      throw error;
    }
  },

  async approvePayment(paymentId) {
    const { error } = await supabase?.rpc('approve_payment', { p_payment_id: paymentId });
    if (error) throw error;
  },

  async rejectPayment(paymentId, note = null) {
    const { error } = await supabase?.rpc('reject_payment', { p_payment_id: paymentId, p_note: note });
    if (error) throw error;
  },

  async updatePlan(planId, updates) {
    const { error } = await supabase?.from('billing_plans')?.update({
      name: updates?.name,
      plan_type: updates?.planType,
      credits: updates?.credits,
      duration_days: updates?.durationDays,
      price_dzd: updates?.priceDzd,
      description: updates?.description,
      is_active: updates?.isActive,
      sort_order: updates?.sortOrder,
      updated_at: new Date().toISOString()
    })?.eq('id', planId);
    if (error) throw error;
  },

  async updatePaymentSettings(settings) {
    const { error } = await supabase?.from('payment_settings')?.update({
      ccp_number: settings?.ccpNumber,
      rip_number: settings?.ripNumber,
      baridimob_number: settings?.baridimobNumber,
      instructions: settings?.instructions,
      updated_at: new Date().toISOString()
    })?.eq('id', 1);
    if (error) throw error;
  }
};

export default billingService;
